import React, { useState } from 'react';
import { tagText } from '../../../lib/assets';
import axios from 'axios';
import { Link, useOutletContext } from 'react-router-dom';
import Diagnostics from '../../Diagnostics';
import DevicePower from '../DevicePower';
import { contractorAuth } from '../../../lib/contractorAuth';
import { fmtDateTime } from '../../../lib/when';
import { ago, ATTACHED_WORD, stateWord, stateTone, signedInSuffix } from '../assetView';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * DEVICE: what the agent last said, what is plugged into it, the
 * diagnostics, lost mode and the sign-in history. A printer or a switch
 * with no agent shows its network facts here instead; a device with
 * nothing to show at all says so, calmly - the tab is always there.
 */
export default function DeviceTab() {
  const { data, asset: a, write, admin, portal, meta, reload, unlinkDevice, unlinkError } = useOutletContext();
  const facts = a.facts;
  const [ruleError, setRuleError] = useState('');
  const setRule = async (childId, v) => {
    setRuleError('');
    try {
      await axios.put(`${API_BASE}/assets/${childId}`, { moves_with_parent: v === '' ? null : v === 'true' }, contractorAuth() || {});
      await reload();
    } catch (e) {
      setRuleError(e.response?.data?.error || 'Could not save that.');
    }
  };
  const networked = 'ip_address' in a;
  const nothing = !data.device && !facts && !networked && !(data.children?.length > 0);
  // Diagnostics: staff always (the server decides what they may ask); a
  // contractor only when the firm lets contractors ask at all.
  const diagnostics = Boolean(data.device) && (!portal || meta?.diagnostics_contractor === true);

  return (
    <div className="as-tab as-tab-device">
      {nothing && (
        <section className="as-panel">
          <h2>Device</h2>
          <p className="as-empty">
            Nothing has reported from this device. A laptop or desktop reports in once its Jarvis agent is
            installed; a printer or network device is seen on the network once a scan finds it.
          </p>
        </section>
      )}
      {(data.device || facts) && <DevicePanel asset={a} device={data.device} write={write} admin={admin} onChanged={reload} />}
      {networked && (
        <section className="as-panel">
          <h2>On the network</h2>
          <Facts rows={[
            ['IP address', a.ip_address],
            ['MAC address', a.mac_address],
            ['Firmware', a.firmware],
            ['Admin page', a.admin_url ? <a href={a.admin_url} target="_blank" rel="noreferrer">{a.admin_url.replace(/^https?:\/\//, '')}</a> : null],
            ['Last seen', a.last_seen_at ? `${ago(a.last_seen_at)}${a.last_seen?.by_tag ? ` by ${a.last_seen.by_tag}` : ''}${a.last_seen?.via === 'scan' ? ' (network scan)' : a.last_seen?.via === 'attached' ? ' (plugged into it)' : ''}` : null]
          ]} />
          {!a.ip_address && !a.mac_address && !a.last_seen_at && (
            <p className="as-quiet">Nothing has seen it on the network yet.</p>
          )}
        </section>
      )}
      {(data.children?.length > 0 || facts?.attached) && (
        <section className="as-panel">
          <h2>Plugged into it</h2>
          {unlinkError && <p className="as-error" role="alert">{unlinkError}</p>}
          {ruleError && <p className="as-error" role="alert">{ruleError}</p>}
          {data.children?.length > 0 && (
            <ul className="as-attached">
              {data.children.map((c) => (
                <li key={c.id}>
                  <Link to={`/assets/${c.id}`}>{tagText(c)}</Link>
                  {` · ${[c.category_name, c.manufacturer, c.model].filter(Boolean).join(' ')}`}
                  {c.serial_number && <span className="as-quiet">{` · ${c.serial_number}`}</span>}
                  {c.last_seen_at && <span className="as-quiet">{` · seen ${ago(c.last_seen_at)}`}</span>}
                  {'location_path' in c && <span className="as-quiet">{` · ${c.location_path || 'no place'}`}</span>}
                  {write && !portal && 'moves_with_parent' in c && (
                    <label className="as-quiet" style={{ marginLeft: '0.5rem' }}>
                      {`When ${tagText(a)} moves `}
                      <select data-testid={`rule-${c.asset_tag}`}
                        value={c.moves_with_parent === true ? 'true' : c.moves_with_parent === false ? 'false' : ''}
                        onChange={(e) => setRule(c.id, e.target.value)}>
                        <option value="">Ask each time</option>
                        <option value="true">Goes with it</option>
                        <option value="false">Stays where it is</option>
                      </select>
                    </label>
                  )}
                  {write && !portal && (
                    <button type="button" className="btn btn-plain btn-small" style={{ marginLeft: '0.5rem' }}
                      onClick={() => unlinkDevice(c.id)}>Unlink</button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {facts?.attached && (
            <>
              <p className="as-quiet" style={{ marginTop: data.children?.length ? '0.75rem' : 0 }}>
                {facts.attached.length
                  ? `Its last report listed ${facts.attached.length} attached device${facts.attached.length === 1 ? '' : 's'}:`
                  : 'Its last report listed nothing attached.'}
              </p>
              {facts.attached.length > 0 && (
                <ul className="as-attached">
                  {facts.attached.map((x, i) => (
                    <li key={i}>
                      <span className="as-chip" data-tone="idle">{ATTACHED_WORD[x.kind] || x.kind}</span>
                      {` ${[x.make, x.model || x.name].filter(Boolean).join(' ') || 'Unnamed'}`}
                      {x.serial && <span className="as-quiet">{` · ${x.serial}`}</span>}
                      {x.size_gb != null && <span className="as-quiet">{` · ${x.size_gb} GB`}</span>}
                      {x.address && <span className="as-quiet">{` · ${x.address}`}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}
      {diagnostics && <Diagnostics path={`/assets/${a.id}/diagnostics`} authHeaders={contractorAuth()?.headers || null} />}
      {/* 116: start-ups, shutdowns, crashes - for whoever looks after it. */}
      {write && !portal && data.device && <DevicePower assetId={a.id} />}
      {facts?.users_recent && facts.users_recent.length > 0 && (
        <section className="as-panel" data-testid="sign-ins">
          <h2>Signed in over the last 14 days</h2>
          <ul className="as-history as-signins">
            {facts.users_recent.map((u, i) => (
              <li key={i}>
                {u.upn || u.account || 'Unknown account'}
                {(u.last_seen || u.at) && <span className="as-when">{`Last ${fmtDateTime(u.last_seen || u.at)}`}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function Facts({ rows }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (shown.length === 0) return <p className="as-quiet">Nothing recorded.</p>;
  return (
    <dl className="as-facts">
      {shown.map(([k, v]) => (
        <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>
      ))}
    </dl>
  );
}

/**
 * What the device's agent last said: when it reported, who was logged in,
 * the OS, whether the disk is encrypted, how full it is, the battery - and
 * where it was, as much as the firm's setting allows.
 *
 * Lost-device mode is switched here, and only here: it takes a reason, and
 * the device's history names who switched it and why. Stopping the agent
 * (a machine sold or wiped) is an administrator's, here and in the page's
 * More menu alike. "Make <name> the holder" lives in the page's header now
 * (AssetLayout), the one place for it.
 */
export function DevicePanel({ asset, device, write, admin = false, onChanged }) {
  const [lostReason, setLostReason] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');
  const f = asset.facts;
  const e = device?.enrolment;
  if (!device && !f) return null;

  const post = async (path, body, method = 'post') => {
    setError('');
    try {
      await axios({ method, url: `${API_BASE}/assets/${asset.id}/${path}`, data: body, ...(contractorAuth() || {}) });
      setAsking(false);
      setLostReason('');
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not work.');
    }
  };

  const silentDays = asset.last_heartbeat_at
    ? (Date.now() - new Date(asset.last_heartbeat_at).getTime()) / 86400000 : null;
  const quiet = e && (silentDays === null || silentDays > (device?.offline_days || 14));
  const loc = asset.last_location;

  return (
    <section className="as-panel" data-testid="device-report">
      <h2>Device</h2>
      {!e && !f && <p className="as-quiet">No agent is installed on this device.</p>}
      {!e && f && <p className="as-quiet">Its agent has been stopped. The last report is below.</p>}
      {e && (
        <p>
          <span className="as-chip" data-tone={quiet ? 'warning' : 'success'}>
            {quiet ? 'Not reporting' : 'Reporting'}
          </span>
          {` Last report ${ago(asset.last_heartbeat_at)}; last checked in ${ago(e.last_seen_at)}. `}
          <span className="as-quiet">{`${e.platform === 'macos' ? 'Mac' : 'Windows'} agent ${e.agent_version || ''}`}</span>
        </p>
      )}
      {asset.lost_at && (
        <p className="as-note" data-tone="warning">
          {`Marked lost ${ago(asset.lost_at)}: ${asset.lost_reason}. Its agent reports a precise location while this is on.`}
        </p>
      )}
      {error && <p className="as-error" role="alert">{error}</p>}
      {f && (
        <dl className="as-facts">
          {/* The register's holder against the account signed in: a person
              whose id differs is not who the register says; a contractor
              holder (093) has no account to compare, so the line says the
              register puts it with a contractor rather than calling it wrong. */}
          <dt>Signed in</dt>
          <dd>
            {f.user_upn || f.user_account || 'Nobody'}{signedInSuffix(asset)}
            {f.users_recent && f.users_recent.length > 0 && (
              <span className="as-sub">
                {`Signed in over the last 14 days: ${f.users_recent.map((u) => u.upn || u.account).filter(Boolean).join(', ')}`}
              </span>
            )}
          </dd>
          <dt>System</dt><dd>{[f.os, f.os_build && `build ${f.os_build}`].filter(Boolean).join(', ') || '—'}{f.pending_reboot ? ' · waiting to restart' : ''}</dd>
          <dt>Encryption</dt><dd><span className="as-chip" data-tone={stateTone(f.encryption)}>{stateWord(f.encryption)}</span>{f.encryption_method ? ` ${f.encryption_method}` : ''}</dd>
          <dt>Firewall</dt><dd><span className="as-chip" data-tone={stateTone(f.firewall)}>{stateWord(f.firewall)}</span></dd>
          <dt>Secure Boot</dt><dd>{stateWord(f.secure_boot)}</dd>
          <dt>Protection</dt><dd>{(f.endpoint_protection || []).join(', ') || 'None reported'}</dd>
          <dt>Processor</dt><dd>{[f.cpu, f.ram_gb && `${f.ram_gb} GB memory`].filter(Boolean).join(' · ') || '—'}</dd>
          <dt>Disks</dt>
          <dd>
            {(f.disks || []).map((d) => {
              const used = d.size_gb ? Math.round(((d.size_gb - d.free_gb) / d.size_gb) * 100) : null;
              return (
                <span key={d.name} style={{ display: 'block' }}>
                  {`${d.name} ${d.free_gb} GB free of ${d.size_gb} GB `}
                  {used !== null && <span className="as-chip" data-tone={used >= 90 ? 'critical' : used >= 80 ? 'warning' : 'idle'}>{`${used}% full`}</span>}
                </span>
              );
            })}
            {(f.disks || []).length === 0 && '—'}
          </dd>
          {f.battery_health_pct != null && (
            <><dt>Battery</dt><dd>{`${f.battery_health_pct}% of its design capacity`}{f.battery_cycles != null ? `, ${f.battery_cycles} cycles` : ''}</dd></>
          )}
          <dt>Where</dt>
          <dd>
            {!loc && 'Not recorded'}
            {loc?.where === 'office' && `In the ${loc.office}${loc.location_path ? ` (${loc.location_path})` : ''}`}
            {loc?.where === 'client' && `At a client’s site: ${loc.office}${loc.location_path ? ` (${loc.location_path})` : ''}`}
            {loc?.where === 'away' && 'Away from the office'}
            {loc?.mode === 'precise' && loc.status === 'recorded' && (
              <a href={`https://www.openstreetmap.org/?mlat=${loc.lat}&mlon=${loc.lng}#map=17/${loc.lat}/${loc.lng}`}
                target="_blank" rel="noreferrer">{`${loc.lat}, ${loc.lng} (within ${loc.accuracy_m} m)`}</a>
            )}
            {loc?.mode === 'precise' && loc.status === 'refused' && 'The person at the machine has location switched off'}
            {loc?.mode === 'precise' && loc.status === 'unavailable' && 'Precise location not available on this machine'}
            {loc?.at && <span className="as-quiet">{` · ${ago(loc.at)}`}</span>}
          </dd>
        </dl>
      )}
      {write && (
        <div className="as-actions" style={{ marginBottom: 0 }}>
          {!asset.lost_at && !asking && e && (
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setAsking(true)}>Mark lost</button>
          )}
          {asset.lost_at && (
            <button type="button" className="btn btn-secondary btn-small" onClick={() => post('found', {})}>Found it</button>
          )}
          {e && admin && (
            <button type="button" className="btn btn-plain btn-small"
              onClick={() => post('enrolment', { reason: 'Stopped from its page' }, 'delete')}>Stop its agent</button>
          )}
        </div>
      )}
      {asking && (
        <form className="as-inline-form" onSubmit={(ev) => { ev.preventDefault(); post('lost', { reason: lostReason }); }}>
          <div className="form-group">
            <label htmlFor="as-lost-reason">Why is it lost? This is recorded with your name.</label>
            <input id="as-lost-reason" value={lostReason} onChange={(ev) => setLostReason(ev.target.value)} required />
          </div>
          <button type="submit" className="btn btn-danger btn-small">Mark lost and locate it</button>
          <button type="button" className="btn btn-plain btn-small" onClick={() => setAsking(false)}>Cancel</button>
        </form>
      )}
    </section>
  );
}
